import React, { FC, useCallback, useState } from "react";
import {
    AutoCompleteSuggestionsInput,
    Checkbox,
    Dialog,
    ButtonProps,
    Input,
    Suggestion,
    URLInput,
} from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";
import { INTEGRATIONS } from "./IntegrationDropdown";

/**
 * A trimmed example of the flat payload, shown when the simplified option is on so a user can
 * see what they will receive before connecting. The full key list lives in the backend's
 * docs/WEBHOOK_PAYLOAD.md — keep this short rather than exhaustive.
 */
const FLAT_PAYLOAD_EXAMPLE = `{
  "lead_id": "3f7c1e88-...",
  "captured_at": "2026-08-12T14:03:11.000Z",
  "first_name": "Jane",
  "last_name": "Doe",
  "full_name": "Jane Doe",
  "email": "jane@example.com",
  "phone": "5555550123",
  "zip": "84043",
  "message": "AC is out, need someone today",
  "source": "form-widget",
  "urgency": "HIGH",
  "contact_type": "SERVICE_REQUEST",
  "form_help_type": "schedule_now",
  "utm_source": "google"
}`;

// Filter out "none", "other", and "webhook" and convert to Suggestion format for autocomplete
const SERVICE_SUGGESTIONS: Suggestion[] = INTEGRATIONS.filter((i) => i.id !== "none" && i.id !== "other").map((i) => ({
    id: i.id,
    label: i.name,
}));

export interface WebhookConnectionProps extends IntegrationConnectionProps {
    integration: "webhook";
    endpoint: string;
    httpHeaders?: string;
    service?: string;
    /**
     * Send the flat, single level snake_case payload instead of the nested one.
     */
    flattenPayload?: boolean;
}

export interface WebhookIntegrationCardProps extends IntegrationCardProp {
    endpoint?: string;
    httpHeaders?: string;
    service?: string;
    /**
     * The currently configured value, so re-opening a connected integration shows its real state.
     */
    flattenPayload?: boolean;
    /**
     * Optional body content for the dialog that displays below the endpoint input box.
     */
    bodyContent?: string | JSX.Element;
    /**
     * Connect Webhook
     */
    onConnect?(props: WebhookConnectionProps): void;
}

export const WebhookIntegrationCard: FC<WebhookIntegrationCardProps> = (props) => {
    const { checked, disabled, isLinked, isActive, onDisconnect, onConnect } = props;

    const [endpoint, setEndpoint] = useState<string>(props.endpoint || "");
    const [httpHeaders, setHttpHeaders] = useState<string>(props.httpHeaders || "");
    const [service, setService] = useState<string>(props.service || "");
    const [flattenPayload, setFlattenPayload] = useState<boolean>(props.flattenPayload || false);
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const isValidUrl = (url: string): boolean => {
        if (!url) return false;
        try {
            new URL(url);
            return url.startsWith("http://") || url.startsWith("https://");
        } catch {
            return false;
        }
    };

    const isValidJson = (str: string): boolean => {
        if (!str) return true; // Empty is okay (optional)
        try {
            const parsed = JSON.parse(str);
            return typeof parsed === "object" && !Array.isArray(parsed);
        } catch {
            return false;
        }
    };

    const handleWebhookClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            // pop up modal
            setShowDialog(true);
        }
    };

    const handleConnectWebhook = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "webhook",
                endpoint,
                httpHeaders: httpHeaders || undefined,
                service: service || undefined,
                flattenPayload,
            });
        }
        setShowDialog(false);
    }, [onConnect, endpoint, httpHeaders, service, flattenPayload]);

    const handleDisconnectWebhook = useCallback(() => {
        if (typeof onDisconnect === "function") {
            onDisconnect();
            setShowDialog(false);
        }
    }, [onDisconnect]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: () => {
                setShowDialog(false);
            },
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            disabled: !endpoint || !isValidUrl(endpoint) || !isValidJson(httpHeaders),
            onClick: handleConnectWebhook,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/webhooks.png"
                text="Webhook"
                href={() => "/integrations/webhook"}
                onClick={handleWebhookClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnectWebhook : undefined}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="Webhook Setup"
                onClose={() => {
                    setShowDialog(false);
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <div>
                        <p>Connect a webhook endpoint to receive lead data in real-time.</p>
                        <p>This integration works with Zapier Catch Hooks, Make.com, or any custom webhook endpoint.</p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <URLInput
                            label="Webhook Endpoint URL"
                            value={endpoint}
                            onChange={(value) => setEndpoint(String(value))}
                        />
                        {endpoint && !isValidUrl(endpoint) && (
                            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                Please enter a valid HTTP or HTTPS URL
                            </p>
                        )}
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <AutoCompleteSuggestionsInput
                            label="Service (Optional)"
                            suggestions={SERVICE_SUGGESTIONS}
                            echo={true}
                            variation="outlined"
                            value={service}
                            placeholder="Select or enter a service"
                            onChange={(selection) => {
                                const value = typeof selection === "string" ? selection : selection.label;
                                setService(value);
                            }}
                        />
                        <p style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                            Associate this webhook with an existing service integration.
                        </p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="HTTP Headers (Optional, JSON format)"
                            value={httpHeaders}
                            multiline
                            rows={4}
                            onChange={(value) => setHttpHeaders(String(value))}
                            placeholder='{"Authorization": "Bearer token"}'
                        />
                        {httpHeaders && !isValidJson(httpHeaders) && (
                            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                Invalid JSON format. Must be a valid JSON object.
                            </p>
                        )}
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Checkbox
                            checked={flattenPayload}
                            label="Send simplified (flat) payload"
                            onChange={(value) => setFlattenPayload(value)}
                        />
                        <p style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                            Sends a single-level JSON object with snake_case field names, which is easier to map in most
                            webhook tools. Leave this off if you already have a working integration — turning it on
                            changes the shape of everything we send.
                        </p>
                        {flattenPayload && (
                            <pre
                                style={{
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    marginTop: "8px",
                                    overflowX: "auto",
                                    padding: "8px",
                                }}
                            >
                                {FLAT_PAYLOAD_EXAMPLE}
                            </pre>
                        )}
                    </div>
                    {props.bodyContent && <div>{props.bodyContent}</div>}
                    <div>
                        <b>What is a webhook?</b>
                        <p>
                            A webhook is an HTTP endpoint that receives data when events occur in your app. When a lead
                            is captured, we'll send a POST request to your endpoint with the lead data.
                        </p>
                    </div>
                    <div>
                        <b>What data will be sent?</b>
                        <p>
                            We will automatically send lead information including customer name, phone number, email,
                            and any other captured data to your webhook endpoint in JSON format.
                        </p>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
